import React, { FunctionComponent, useMemo } from 'react'
import { useTranslation } from '../../../utils/convergence'
import { usePclPaymentOptions, usePbiPaymentOption, useIsAfterpayFinanceFeeEligible } from '../../../state'
import { PaymentOption, DEFAULT_PAYMENT_TYPE_SORT_ORDER } from '../../../model'
import { PaymentOptionLabel } from '../../PaymentOptionLabel'
import { Heading, Radio, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../../utils/responsive'
import { ConsumerLendingTagline } from '../../ConsumerLendingTagline'
import { PaymentType } from '@afterpay/types'
import styles from './style.module.css'

interface PaymentOptionsRadioGroupProps {
  lock: boolean
  selectedPaymentOptionLocal?: PaymentOption
  setSelectedPaymentOptionLocal: (paymentOptions: PaymentOption) => void
  paymentTypeSortOrder?: PaymentType[]
}

export const PaymentOptionsRadioGroup: FunctionComponent<PaymentOptionsRadioGroupProps> = ({
  selectedPaymentOptionLocal,
  setSelectedPaymentOptionLocal,
  lock,
  paymentTypeSortOrder,
}) => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { t } = useTranslation()
  const { textSize } = useCheckoutSizes()
  const pclPaymentOptions = usePclPaymentOptions()
  const pbiPaymentOption = usePbiPaymentOption()
  const isAFF = useIsAfterpayFinanceFeeEligible()

  const sortOrder = paymentTypeSortOrder ?? DEFAULT_PAYMENT_TYPE_SORT_ORDER
  const isPbiFirst =
    sortOrder.includes(PaymentType.PBI) && sortOrder.includes(PaymentType.PCL) && sortOrder.indexOf(PaymentType.PBI) < sortOrder.indexOf(PaymentType.PCL)

  const sortedPclPaymentOptions = useMemo(
    () =>
      isPbiFirst
        ? [...pclPaymentOptions].sort((a, b) => parseFloat(a.interestAmount?.amount ?? '0') - parseFloat(b.interestAmount?.amount ?? '0'))
        : pclPaymentOptions,
    [pclPaymentOptions, isPbiFirst]
  )

  const renderPaymentOption = (paymentOption: PaymentOption): JSX.Element => {
    const selected = paymentOption.id === selectedPaymentOptionLocal?.id

    return (
      <Radio.Button
        testNameSpace={`payment-option-${paymentOption.id}`}
        key={`payment-option-${paymentOption.id}`}
        id={`payment-option-${paymentOption.id}`}
        value={paymentOption.id.toString()}
        reversed
        roundedBorder
        selectedBorder={selected && !isArcadeDesignLanguage}
        label={() => <PaymentOptionLabel paymentOption={paymentOption} selected={selected} />}
      />
    )
  }

  const pclSection = (
    <React.Fragment key='pcl-section'>
      <div className={styles.pclTagline}>
        <ConsumerLendingTagline />
      </div>
      {sortedPclPaymentOptions.map(renderPaymentOption)}
    </React.Fragment>
  )

  const pbiSection =
    pbiPaymentOption !== undefined ? (
      <React.Fragment key='pbi-section'>
        <div className={styles.pbiHeading}>
          <Heading size={textSize}>
            {t('consumerLending:payInFourTagline', { context: isAFF ? 'aff' : '', interval: pbiPaymentOption.installmentsCount })}
          </Heading>
        </div>
        {renderPaymentOption(pbiPaymentOption)}
      </React.Fragment>
    ) : null

  return (
    <Radio.Group
      id='payment-options'
      name='payment-options'
      testNameSpace='payment-options'
      kind='Option'
      labelWidth='Full'
      labelPlacement='Top'
      value={selectedPaymentOptionLocal?.id.toString()}
      disabled={lock}
      onClick={(e) => {
        const clickedPaymentOptionId = Number((e.target as HTMLButtonElement).value)
        const allOptions = pbiPaymentOption ? [...pclPaymentOptions, pbiPaymentOption] : pclPaymentOptions
        const match = allOptions.find((paymentOption) => paymentOption.id === clickedPaymentOptionId)
        if (match) {
          setSelectedPaymentOptionLocal(match)
        }
      }}
    >
      {sortOrder.map((type) => {
        if (type === PaymentType.PCL) return pclSection
        if (type === PaymentType.PBI) return pbiSection
        return null
      })}
    </Radio.Group>
  )
}
